/**
 * TermsOfService.tsx — Shield Terms of Service.
 *
 * Route: /terms — publicly accessible.
 * 16 sections with table of contents + anchor links.
 * [PLACEHOLDER] text marks areas to fill before launch.
 */

import React, { useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import SecurityIcon from '@mui/icons-material/Security';

// ── Shared subcomponents ──────────────────────────────────────────────────────

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <Box id={id} sx={{ mb: 5, scrollMarginTop: 80 }}>
      <Typography variant="h5" fontWeight={700} mb={2} color="primary.main">{title}</Typography>
      <Box>{children}</Box>
    </Box>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" paragraph sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

function UL({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      {items.map((item) => (
        <Box component="li" key={item} sx={{ mb: 0.75 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── TOC sections ──────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'acceptance',           title: '1. Acceptance of Terms' },
  { id: 'definitions',          title: '2. Definitions' },
  { id: 'account',              title: '3. Account Registration' },
  { id: 'service',              title: '4. The Service' },
  { id: 'acceptable-use',       title: '5. Acceptable Use' },
  { id: 'subscriptions',        title: '6. Subscriptions and Billing' },
  { id: 'trial',                title: '7. Free Trial' },
  { id: 'ip',                   title: '8. Intellectual Property' },
  { id: 'data',                 title: '9. Your Data' },
  { id: 'confidentiality',      title: '10. Confidentiality' },
  { id: 'disclaimers',          title: '11. Disclaimers' },
  { id: 'liability',            title: '12. Limitation of Liability' },
  { id: 'indemnification',      title: '13. Indemnification' },
  { id: 'termination',          title: '14. Termination' },
  { id: 'governing-law',        title: '15. Governing Law' },
  { id: 'changes',              title: '16. Changes to These Terms' },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function TermsOfService() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ── Navbar ─────────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, sm: 5 }, py: 2,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: 'rgba(13,17,23,0.92)', backdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <SecurityIcon sx={{ color: '#00E5FF', fontSize: 26, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
          <Typography variant="h6" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {user ? (
            <Button variant="contained" component={RouterLink} to="/dashboard" sx={{ color: '#FFFFFF' }}>
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button variant="outlined" component={RouterLink} to="/login" sx={{ color: '#FFFFFF', borderColor: '#FFFFFF', '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                Sign In
              </Button>
              <Button variant="contained" component={RouterLink} to="/signup" sx={{ fontWeight: 700 }}>
                Start Free Trial
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 5 }, py: { xs: 5, md: 8 } }}>

        {/* Header */}
        <Box mb={5}>
          <Typography variant="h3" fontWeight={800} mb={1.5}>Terms of Service</Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: [LAST UPDATED DATE] · Effective: [EFFECTIVE DATE]
          </Typography>
          <Divider sx={{ mt: 3 }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>

          {/* TOC sidebar */}
          <Box
            component="aside"
            sx={{
              display: { xs: 'none', md: 'block' },
              width: 240,
              flexShrink: 0,
              position: 'sticky',
              top: 80,
            }}
          >
            <Typography variant="caption" color="text.disabled" fontWeight={700}
              sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
              Contents
            </Typography>
            <Stack spacing={0.5}>
              {SECTIONS.map(({ id, title }) => (
                <Link
                  key={id}
                  href={`#${id}`}
                  underline="none"
                  sx={{
                    fontSize: 13, color: 'text.secondary', display: 'block', py: 0.4,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {title}
                </Link>
              ))}
            </Stack>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            <Section id="acceptance" title="1. Acceptance of Terms">
              <P>
                By accessing or using the Shield cloud security and AI monitoring platform ("Service") provided by
                [COMPANY NAME] ("[COMPANY NAME]", "we", "us"), you agree to be bound by these Terms of Service
                ("Terms"). If you are agreeing on behalf of a company or other legal entity, you represent that you
                have the authority to bind that entity to these Terms.
              </P>
              <P>
                If you do not agree to these Terms, you may not access or use the Service. These Terms incorporate
                our Privacy Policy by reference.
              </P>
            </Section>

            <Section id="definitions" title="2. Definitions">
              <UL items={[
                '"Account" means a registered user account on the Service.',
                '"Customer Data" means data you upload, connect, or generate through your use of the Service.',
                '"Subscription" means a paid or trial plan giving you access to the Service.',
                '"Authorised User" means an employee or contractor you permit to access your Account.',
                '"Documentation" means the official technical guides and help content we publish.',
                '"Shield Platform" means the software, APIs, dashboards, and services we operate.',
              ]} />
            </Section>

            <Section id="account" title="3. Account Registration">
              <P>
                To use the Service you must register for an Account. You agree to: (a) provide accurate and complete
                information; (b) keep your credentials confidential; (c) notify us immediately of any unauthorised
                access; and (d) accept responsibility for all activity that occurs under your Account.
              </P>
              <P>
                You must be at least 18 years old to create an Account. Accounts may not be shared or transferred
                without our written consent.
              </P>
            </Section>

            <Section id="service" title="4. The Service">
              <P>
                [COMPANY NAME] grants you a limited, non-exclusive, non-transferable, revocable licence to access
                and use the Service during your Subscription term solely for your internal business security
                operations.
              </P>
              <P>
                We may update, modify, or discontinue features of the Service at any time. We will provide reasonable
                notice of material changes that affect paid Subscribers. The Service is provided on an "as-is" and
                "as-available" basis subject to our uptime commitments in your Subscription plan.
              </P>
            </Section>

            <Section id="acceptable-use" title="5. Acceptable Use">
              <P>You agree not to use the Service to:</P>
              <UL items={[
                'Violate any applicable law, regulation, or third-party rights',
                'Upload, transmit, or store malicious code, malware, or offensive content',
                'Attempt to gain unauthorised access to any third-party systems or data',
                'Reverse-engineer, decompile, or disassemble any part of the Service',
                'Resell, sublicense, or provide the Service to third parties without our written consent',
                'Conduct automated scraping, crawling, or data mining against the Service beyond what our API allows',
                'Interfere with the integrity or performance of the Service or its underlying infrastructure',
                'Use the Service in a way that competes with or benchmarks against [COMPANY NAME]\'s products',
              ]} />
              <P>
                Violation of this section may result in immediate suspension or termination of your Account.
              </P>
            </Section>

            <Section id="subscriptions" title="6. Subscriptions and Billing">
              <P>
                Access to paid features requires a valid Subscription. Subscriptions renew automatically at the end of
                each billing period (monthly or annual) unless cancelled before the renewal date. Prices are listed
                on our Pricing page and may change with 30 days' notice to existing Subscribers.
              </P>
              <P>
                All fees are charged in [CURRENCY] and are exclusive of applicable taxes (including VAT, GST, or
                similar), which are your responsibility. Payments are processed by Stripe. You authorise us to charge
                your payment method on file at each renewal.
              </P>
              <P>
                Refunds are not provided for partial periods. If you downgrade your plan, the change takes effect at
                the start of the next billing period. Past-due accounts may be suspended until outstanding balances
                are paid.
              </P>
            </Section>

            <Section id="trial" title="7. Free Trial">
              <P>
                We offer a 14-day free trial of the Service. No credit card is required to start a trial. You may
                cancel at any time during the trial at no charge. At the end of the trial, the Service will pause
                unless you provide valid payment details and activate a paid Subscription.
              </P>
              <P>
                Trial accounts that are not converted to paid Subscriptions have their data retained for 30 days
                after trial expiry, after which data is permanently deleted. [COMPANY NAME] reserves the right to
                modify or discontinue trial availability at any time.
              </P>
            </Section>

            <Section id="ip" title="8. Intellectual Property">
              <P>
                The Service, including all software, algorithms, designs, trademarks, and documentation, is owned by
                [COMPANY NAME] or its licensors and is protected by intellectual property laws. These Terms do not
                transfer any ownership rights to you.
              </P>
              <P>
                You grant [COMPANY NAME] a limited licence to process your Customer Data solely to provide the
                Service. You retain all ownership rights to your Customer Data.
              </P>
            </Section>

            <Section id="data" title="9. Your Data">
              <P>
                You are responsible for the accuracy, quality, and legality of your Customer Data and for ensuring
                you have the right to provide it to us for processing. You may export or delete your Customer Data
                at any time from your account settings or by contacting support.
              </P>
              <P>
                We process your Customer Data in accordance with our Privacy Policy and applicable data protection
                laws. Cloud credentials are encrypted at rest using AES-256-GCM and are never accessed by our staff
                except where required to provide the Service or comply with legal obligations.
              </P>
            </Section>

            <Section id="confidentiality" title="10. Confidentiality">
              <P>
                Each party agrees to keep confidential the other party's non-public information that is designated
                as confidential or that reasonably should be understood to be confidential. This obligation does not
                apply to information that: (a) is or becomes publicly known through no fault of the receiving party;
                (b) was already known to the receiving party at the time of disclosure; or (c) is required to be
                disclosed by law.
              </P>
            </Section>

            <Section id="disclaimers" title="11. Disclaimers">
              <P>
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT
                WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF SECURITY VULNERABILITIES.
              </P>
              <P>
                SECURITY SCAN RESULTS ARE PROVIDED FOR INFORMATIONAL PURPOSES ONLY. [COMPANY NAME] MAKES NO
                GUARANTEE THAT THE SERVICE WILL DETECT ALL SECURITY ISSUES OR PREVENT ALL SECURITY INCIDENTS.
                YOU ARE SOLELY RESPONSIBLE FOR YOUR SECURITY POSTURE AND COMPLIANCE OBLIGATIONS.
              </P>
            </Section>

            <Section id="liability" title="12. Limitation of Liability">
              <P>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, [COMPANY NAME]'S TOTAL LIABILITY TO YOU FOR
                ANY CLAIMS ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO
                [COMPANY NAME] IN THE 12 MONTHS PRECEDING THE CLAIM.
              </P>
              <P>
                IN NO EVENT SHALL [COMPANY NAME] BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, EVEN IF ADVISED OF THE
                POSSIBILITY OF SUCH DAMAGES.
              </P>
            </Section>

            <Section id="indemnification" title="13. Indemnification">
              <P>
                You agree to defend, indemnify, and hold harmless [COMPANY NAME], its directors, employees, and
                agents from any claims, damages, losses, costs, and expenses (including reasonable legal fees)
                arising from: (a) your use of the Service in violation of these Terms; (b) your Customer Data;
                or (c) your violation of any applicable law or third-party right.
              </P>
            </Section>

            <Section id="termination" title="14. Termination">
              <P>
                Either party may terminate a Subscription at any time. You may cancel through your billing settings
                or by contacting support. Cancellation takes effect at the end of the current billing period;
                no refund is issued for the remainder of that period.
              </P>
              <P>
                We may suspend or terminate your Account immediately if you breach these Terms, fail to pay,
                or if continued access would expose us or third parties to legal risk. Upon termination, your
                right to use the Service ceases and we will delete your Customer Data after the applicable
                retention period described in our Privacy Policy.
              </P>
            </Section>

            <Section id="governing-law" title="15. Governing Law">
              <P>
                These Terms are governed by the laws of [JURISDICTION], without regard to its conflict of law
                provisions. Any dispute arising from these Terms shall be subject to the exclusive jurisdiction
                of the courts of [JURISDICTION]. Nothing in this section prevents either party from seeking
                injunctive or other equitable relief.
              </P>
            </Section>

            <Section id="changes" title="16. Changes to These Terms">
              <P>
                We may modify these Terms at any time. If changes are material, we will notify you by email or
                through the Service at least 30 days before they take effect. Your continued use of the Service
                after that date constitutes acceptance of the updated Terms.
              </P>
              <P>
                If you have questions about these Terms, contact us at legal@[CONTACT EMAIL].
              </P>
            </Section>

          </Box>
        </Box>
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <Box component="footer" sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.disabled">
          © {new Date().getFullYear()} [COMPANY NAME] ·{' '}
          <Link href="/privacy" color="text.disabled" underline="hover">Privacy Policy</Link>
          {' · '}
          <Link href="/contact" color="text.disabled" underline="hover">Contact</Link>
        </Typography>
      </Box>
    </Box>
  );
}
